import { Badge } from '@figma/fpl-components';
import { Icon24Enhance } from '@figma/fpl-icons';
import { useTranslation } from 'react-i18next';
import { I18N_KEYS } from '@/language/keys';
import { color } from '@/colors';
import { Tooltip } from '@/UI/Tooltip/Tooltip';
import { FPL_ICON_COLOR_VAR } from '@/UI/Icons/fplIcon';
import { AppCheckbox } from '@/UI/AppCheckbox/AppCheckbox';
import { ButtonContained } from '@/UI/Buttons/AppButton';
import { useFplMigrationFlag } from '@/contexts/FplMigrationContext';
import { useNodeData, useNodeDataNullable, useParameterView } from '@/components/Recipe/FlowContext';
import { SEEDANCE_25_DRAFT_RESOLUTION, SEEDANCE_25_MODELS } from './consts/seedance-25.const';
import { useIsSeedance25Draft, useSeedance25DraftResolution } from './useSeedance25DraftMode';
import { isSeedance25DraftResult, useSeedance25Enhance, useSelectedModelResult } from './useSeedance25Enhance';
import type { SxProps } from '@/UI/theme.exports';
import type { AiModelData } from '@/types/nodes/model';
import type { UploadedAsset } from '@/types/api/assets';
import type { FileKind } from '@/designer/designer';

/**
 * The run buttons' height: `RunModelButton` sets 36px on MUI buttons, while FPL buttons ignore `sx`
 * and render at their `lg` size, 32px.
 */
const RUN_BUTTON_HEIGHT = { mui: 36, fpl: 32 } as const;

/** Sized like the Run Model button it sits next to. */
const FOOTER_BUTTON_SX: SxProps = {
  height: `${RUN_BUTTON_HEIGHT.mui}px`,
  borderRadius: '8px',
  px: 1.5,
  fontSize: '0.875rem',
  fontWeight: 500,
  textTransform: 'none',
  whiteSpace: 'nowrap',
  boxShadow: 'none',
  // FPL icons read their color from this variable; follow the label's, including when disabled.
  [FPL_ICON_COLOR_VAR]: 'currentColor',
  // Disabled, the fill is nearly transparent; an inset outline keeps the button's edge visible, so it
  // doesn't look smaller than the outlined Run draft beside it. (FPL buttons keep a solid disabled fill.)
  // `:disabled`, not `.Mui-disabled`: ButtonContained spreads `sx` over its own `.Mui-disabled` block,
  // so reusing that key would drop its disabled fill, text color and border.
  '&:disabled': { boxShadow: `inset 0 0 0 1px ${color.White16_T}` },
};

/**
 * Draft mode for a Seedance 2.5 node's footer: whether the node is a Seedance 2.5 node, whether
 * Draft is on, and a setter. Also keeps the resolution at 480p while Draft is on, so a Draft turned
 * on from the footer with the properties pane closed is still valid.
 */
export function useSeedance25NodeDraftMode(nodeId: string) {
  const view = useParameterView(nodeId);
  const modelName = useNodeData<AiModelData>(nodeId).model?.name;
  const isSeedance25 = modelName !== undefined && SEEDANCE_25_MODELS.has(modelName);
  const isDraft = useIsSeedance25Draft(view, modelName);
  useSeedance25DraftResolution(view, modelName, isDraft);
  const canToggle = isSeedance25 && view.getParameterIds().includes('draft') && !view.isParameterExposed('draft');
  const setDraft = (value: boolean) => {
    view.setParameterValue('draft', { type: 'boolean', value });
    if (value && !view.isParameterExposed('resolution')) {
      view.setParameterValue('resolution', { type: 'string', value: SEEDANCE_25_DRAFT_RESOLUTION });
    }
  };
  return { isSeedance25, isDraft, canToggle, setDraft };
}

/** The node footer's Draft mode checkbox for a Seedance 2.5 node. */
export function Seedance25DraftModeCheckbox({
  checked,
  disabled,
  onChange,
}: {
  checked: boolean;
  disabled?: boolean;
  onChange: (checked: boolean) => void;
}) {
  const { t } = useTranslation();
  const isFpl = useFplMigrationFlag();
  return (
    <Tooltip
      title={t(I18N_KEYS.RECIPE_MAIN.NODES.MODEL_BASE_NODE.SEEDANCE_ENHANCE.DRAFT_MODE_TOOLTIP)}
      disableInteractive
    >
      <span
        className="nodrag"
        data-testid="seedance-draft-mode-checkbox"
        // As tall as the run buttons beside it, so the checkbox sits on their center line.
        style={{ display: 'flex', alignItems: 'center', height: isFpl ? RUN_BUTTON_HEIGHT.fpl : RUN_BUTTON_HEIGHT.mui }}
      >
        <AppCheckbox
          checked={checked}
          disabled={disabled}
          onChange={(e) => onChange(e.target.checked)}
          label={t(I18N_KEYS.RECIPE_MAIN.NODES.MODEL_BASE_NODE.SEEDANCE_ENHANCE.DRAFT_MODE)}
        />
      </span>
    </Tooltip>
  );
}

/**
 * Generate high res, next to Run draft while Draft mode is on: renders the selected draft result at
 * 1080p on the same node. Disabled unless the selected result is a draft.
 */
export function Seedance25GenerateHighResButton({
  nodeId,
  disabled,
  onError,
}: {
  nodeId: string;
  disabled?: boolean;
  onError: (error: string) => void;
}) {
  const { t } = useTranslation();
  const enhance = useSeedance25Enhance(nodeId, useSelectedModelResult(nodeId), onError);
  const isDisabled = disabled || !enhance.canEnhance || enhance.isRunning;
  return (
    <Tooltip
      title={
        enhance.hasIterators
          ? t(I18N_KEYS.RECIPE_MAIN.NODES.MODEL_BASE_NODE.SEEDANCE_ENHANCE.ITERATORS_TOOLTIP)
          : enhance.isDraft && enhance.isUnavailable
            ? t(I18N_KEYS.RECIPE_MAIN.NODES.MODEL_BASE_NODE.SEEDANCE_ENHANCE.UNAVAILABLE_TOOLTIP)
            : enhance.canEnhance
              ? enhance.tooltip
              : t(I18N_KEYS.RECIPE_MAIN.NODES.MODEL_BASE_NODE.SEEDANCE_ENHANCE.DRAFTS_ONLY_TOOLTIP)
      }
      disableInteractive
    >
      <span>
        <ButtonContained
          mode="filled-light"
          data-testid="seedance-generate-high-res-button"
          startIcon={<Icon24Enhance />}
          disabled={isDisabled}
          onClick={enhance.onClick}
          sx={FOOTER_BUTTON_SX}
        >
          {enhance.isEnhancingThis
            ? t(I18N_KEYS.RECIPE_MAIN.NODES.MODEL_BASE_NODE.SEEDANCE_ENHANCE.GENERATING_HIGH_RES)
            : t(I18N_KEYS.RECIPE_MAIN.NODES.MODEL_BASE_NODE.SEEDANCE_ENHANCE.GENERATE_HIGH_RES)}
        </ButtonContained>
      </span>
    </Tooltip>
  );
}

/**
 * The Draft badge before a Seedance 2.5 draft result's media info, on the node and in full screen.
 * Reads the node from the flow graph, so render it only inside the canvas (where a node id exists).
 */
export function Seedance25DraftBadge({ nodeId, asset }: { nodeId: string; asset?: UploadedAsset | FileKind }) {
  const { t } = useTranslation();
  const modelName = useNodeDataNullable<AiModelData>(nodeId)?.model?.name;
  if (!isSeedance25DraftResult(modelName, asset as UploadedAsset | undefined)) {
    return null;
  }
  return (
    // A flex box, so the badge isn't set on a text baseline in a line box taller than itself.
    <span data-testid="seedance-draft-badge" style={{ display: 'flex' }}>
      <Badge variant="defaultFilled" size="md">
        {t(I18N_KEYS.RECIPE_MAIN.NODES.MODEL_BASE_NODE.SEEDANCE_ENHANCE.DRAFT_BADGE)}
      </Badge>
    </span>
  );
}
